import type {
    ProyectoAdjudicatarioDTO,
    ProyectoContratoMayorDTO,
    ProyectoContratoMenorDTO,
    ProyectoDocumentacionAdjudicatarioDTO,
    ProyectoDocumentacionSubcontratistaDTO,
    ProyectoGastoImputadoFueraSubvencionDTO,
    ProyectoDTO,
    ProyectoInformacionGeneralDTO,
    ProyectoSubcontratistaDTO,
    ProyectoFormDTO,
} from '../../../api/Objetos';
import {createEmptyProyecto} from './emptyProyecto';

type DeepPartial<T> = {
    [K in keyof T]?: T[K] extends Array<infer U>
        ? Array<DeepPartial<U>>
        : T[K] extends object
            ? DeepPartial<T[K]>
            : T[K];
};

function merge<T>(base: T, patch?: DeepPartial<T>): T {
    if (!patch) return base;

    if (Array.isArray(base)) {
        return ((patch as unknown[]) ?? []) as T;
    }

    if (typeof base === 'object' && base !== null) {
        const result: Record<string, unknown> = {...(base as Record<string, unknown>)};

        Object.entries(patch as Record<string, unknown>).forEach(([key, value]) => {
            if (value === undefined) return;

            const current = result[key];
            if (Array.isArray(value)) {
                result[key] = value;
                return;
            }

            if (value && typeof value === 'object' && current && typeof current === 'object' && !Array.isArray(current)) {
                result[key] = merge(current, value as DeepPartial<typeof current>);
                return;
            }

            result[key] = value;
        });

        return result as T;
    }

    return (patch as T) ?? base;
}

export function toFormDTO(proyecto: ProyectoDTO): ProyectoFormDTO {
    const empty = createEmptyProyecto();
    if (!proyecto) return empty;

    return {
        ...empty,
        ...proyecto,
        seguimientoPlataforma: proyecto.seguimientoPlataforma ?? empty.seguimientoPlataforma,
        seguimientoCoFFE: proyecto.seguimientoCoFFE ?? empty.seguimientoCoFFE,
        certificacionesObra: proyecto.certificacionesObra ?? empty.certificacionesObra,
    };
}

export function normalizeProyectoForm(
    input?: DeepPartial<ProyectoFormDTO> | null,
    force = false
): ProyectoFormDTO {
    const source = merge(createEmptyProyecto(), input ?? {});

    if (force) {
        return JSON.parse(JSON.stringify(source)) as ProyectoFormDTO;
    }

    return {
        ...source,
        informacionGeneral: normalizeInformacionGeneral(source.informacionGeneral),
        datosContratos: {
            ...(source.datosContratos ?? {}),
            contratoMayores: (source.datosContratos?.contratoMayores ?? []).map(normalizeContratoMayor),
            contratoMenores: (source.datosContratos?.contratoMenores ?? []).map(normalizeContratoMenor),
            gastosImputadosFueraDeLaSubvencion:
                (source.datosContratos?.gastosImputadosFueraDeLaSubvencion ?? []).map(normalizeGasto),
        }
    };
}

export function prepareProyectoPayload(input: ProyectoFormDTO): ProyectoDTO {
    const normalized = normalizeProyectoForm(JSON.parse(JSON.stringify(input)) as ProyectoFormDTO);

    const payload: ProyectoDTO = {
        ...normalized,
        seguimientoPlataforma: normalized.seguimientoPlataforma,
        seguimientoCoFFE: normalized.seguimientoCoFFE,
        certificacionesObra: normalized.certificacionesObra,
    };

    return deepRemoveZeroIds(payload);
}

function deepRemoveZeroIds<T>(value: T): T {
    if (Array.isArray(value)) {
        return value.map(v => deepRemoveZeroIds(v)) as T;
    }
    if (value && typeof value === 'object') {
        const result: Record<string, unknown> = {};
        Object.entries(value as Record<string, unknown>).forEach(([key, val]) => {
            if (isIdKey(key) && val === 0) return;
            result[key] = deepRemoveZeroIds(val);
        });
        return result as T;
    }
    return value;
}

export function prepareProyectoCreatePayload(input: ProyectoFormDTO): ProyectoDTO {
    const normalized = prepareProyectoPayload(input);
    return deepRemoveIdFields(normalized);
}

export function createDefaultDocumentacionAdjudicatario(): ProyectoDocumentacionAdjudicatarioDTO[] {
    return [];
}

function deepRemoveIdFields<T>(value: T): T {
    if (Array.isArray(value)) {
        return value.map((item) => deepRemoveIdFields(item)) as T;
    }

    if (value && typeof value === 'object') {
        const result: Record<string, unknown> = {};

        Object.entries(value as Record<string, unknown>).forEach(([key, entry]) => {
            if (isIdKey(key)) return;
            result[key] = deepRemoveIdFields(entry);
        });

        return result as T;
    }

    return value;
}

function isIdKey(key: string) {
    return key === 'id' || key === 'datosContratoId';
}

function normalizeInformacionGeneral(input?: DeepPartial<ProyectoInformacionGeneralDTO>) {
    const base = createEmptyProyecto().informacionGeneral ?? {};

    const result: ProyectoInformacionGeneralDTO = {
        ...base,
        ...(input ?? {}),
    };

    result.fechaSolicitud = normalizeDateValue(input?.fechaSolicitud);
    result.fechaConcesionResolucion = normalizeDateValue(input?.fechaConcesionResolucion);

    return result;
}

function normalizeGasto(
    input?: DeepPartial<ProyectoGastoImputadoFueraSubvencionDTO>
): ProyectoGastoImputadoFueraSubvencionDTO {
    return {
        id: undefined,
        documentoContable: '',
        descripcion: '',
        importe: 0,
        observaciones: '',
        ...(input ?? {}),
    };
}

function normalizeAdjudicatario(input?: DeepPartial<ProyectoAdjudicatarioDTO>): ProyectoAdjudicatarioDTO {
    return {
        id: (input?.id && input.id > 0) ? input.id : undefined,
        nombre: '',
        numeroExpediente: '',
        informacionContactos: '',
        ...(input ?? {}),
        documentacion: (input?.documentacion ?? []).map(normalizeDocumentoAdjudicatario),
    };
}

function normalizeDocumentoAdjudicatario(
    input?: DeepPartial<ProyectoDocumentacionAdjudicatarioDTO>
): ProyectoDocumentacionAdjudicatarioDTO {
    return {
        id: undefined,
        nombreDocumento: '',
        presentado: false,
        observaciones: '',
        ...(input ?? {}),
    };
}

function normalizeDocumentoSubcontratista(
    input?: DeepPartial<ProyectoDocumentacionSubcontratistaDTO>
): ProyectoDocumentacionSubcontratistaDTO {
    return {
        id: undefined,
        nombreDocumento: '',
        presentado: false,
        observaciones: '',
        ...(input ?? {}),
    };
}


function normalizeSubcontratista(input?: DeepPartial<ProyectoSubcontratistaDTO>): ProyectoSubcontratistaDTO {
    return {
        id: undefined,
        nombre: '',
        ...(input ?? {}),
        documentacion: (input?.documentacion ?? []).map(normalizeDocumentoSubcontratista),
    };
}

function normalizeContratoMayor(input?: DeepPartial<ProyectoContratoMayorDTO>): ProyectoContratoMayorDTO {
    const result: ProyectoContratoMayorDTO = {
        id: undefined,
        numeroExpediente: '',
        descripcion: '',
        urlPliego: '',
        aprobacionInforme: '',
        aprobacionDecreto: '',
        pcspFechaPublicacion: '',
        pcspFechaLimitePresentacion: '',
        pcspMesaApertura: '',
        controlMinerva: '',
        numeroCLici: '',
        mesasPosteriores: '',
        plazosEjecucion: '',
        ...(input ?? {}),
        adjudicatario: normalizeAdjudicatario(input?.adjudicatario),
        proyectoSubcontratistas: (input?.proyectoSubcontratistas ?? []).map(normalizeSubcontratista),
    };
    // Normalizar fechas usando los nuevos nombres camelCase
    result.aprobacionInforme = normalizeDateValue(input?.aprobacionInforme);
    result.aprobacionDecreto = normalizeDateValue(input?.aprobacionDecreto);
    result.pcspFechaPublicacion = normalizeDateValue(input?.pcspFechaPublicacion);
    result.pcspFechaLimitePresentacion = normalizeDateValue(input?.pcspFechaLimitePresentacion);
    result.pcspMesaApertura = normalizeDateValue(input?.pcspMesaApertura);
    result.controlMinerva = normalizeDateValue(input?.controlMinerva);
    return result;
}

function normalizeContratoMenor(input?: DeepPartial<ProyectoContratoMenorDTO>): ProyectoContratoMenorDTO {
    const { adjudicatario, ...rest } = input ?? {};

    return {
        id: undefined,
        numeroExpediente: '',
        descripcion: '',
        anexosPRTR_presentados: '',
        ...rest,
        adjudicatario: normalizeAdjudicatario(adjudicatario),
        informeMotivandoNecesidad: normalizeDateValue(rest.informeMotivandoNecesidad),
        minerva: normalizeDateValue(rest.minerva),
        informePropuestaResolucion: normalizeDateValue(rest.informePropuestaResolucion),
        decretoDeAdjudicacion: normalizeDateValue(rest.decretoDeAdjudicacion),
        fechaInicioContrato: normalizeDateValue(rest.fechaInicioContrato),
        fechaEstimacionInicial: normalizeDateValue(rest.fechaEstimacionInicial),
        fechaMaximoPresentacionFacturaFinalizacion: normalizeDateValue(rest.fechaMaximoPresentacionFacturaFinalizacion),
        fechaMaximaFinalizacionContrato: normalizeDateValue(rest.fechaMaximaFinalizacionContrato),
    };
}

function normalizeDateValue(value: unknown): string {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return '';

        const isoDateMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
        if (isoDateMatch) return isoDateMatch[1];

        const parsed = new Date(trimmed);
        if (Number.isNaN(parsed.getTime())) return trimmed;
        return parsed.toISOString().slice(0, 10);
    }

    if (Array.isArray(value) && value.length >= 3) {
        const [year, month, day] = value;
        const y = Number(year);
        const m = Number(month);
        const d = Number(day);

        if ([y, m, d].some((part) => !Number.isFinite(part))) return '';

        return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }

    return '';
}
